import wretch from 'wretch';
import FormDataAddon from 'wretch/addons/formData';
import QueryStringAddon from 'wretch/addons/queryString';

import { formContentType, jsonContentType } from 'portal/src/api/constants';
import { isUserReloading } from 'portal/src/auth/UserUtils';

const baseUrl = import.meta.env.VITE_API_URL;

const reloadGuardMiddleware = (next: any) => (url: any, opts: any) => {
  return next(url, opts).catch((error: any) => {
    if (opts.method === 'GET' && isUserReloading()) {
      console.error('Cancelled due to page reload');
      return;
    }
    throw error;
  });
};

const wService = wretch(baseUrl)
  .addon(FormDataAddon)
  .addon(QueryStringAddon)
  .middlewares([reloadGuardMiddleware])
  .options({
    credentials: 'include',
  });

export const getBlobRequest = async (
  url: string,
  params: any = {},
): Promise<Blob> => await wService.url(url).query(params).get().blob();

export const getRequest = async (
  url: string,
  params: any = {},
): Promise<any> => {
  return await wService
    .url(url)
    .query(params)
    .get()
    .res((response) => {
      if (
        response.status === 204 ||
        response.headers.get('content-length') === '0'
      ) {
        return null;
      }
      return response.json();
    });
};

export const postRequest = async (
  url: string,
  params: any = {},
  body: any = {},
  contentType: string = jsonContentType,
  jsonResponse: boolean = false,
): Promise<any> => {
  let service = wService.url(url).query(params);
  if (contentType === formContentType) {
    service = service.formData(body);
  }
  if (contentType === jsonContentType) {
    service = service.json(body).content(jsonContentType);
  }

  const post = service.post().badRequest((err) => {
    throw err['json'];
  });

  if (jsonResponse) {
    return await post.json();
  }

  return await post.text();
};

export const putRequest = async (
  url: string,
  params: any = {},
  body: any = {},
): Promise<any> =>
  await wService
    .url(url)
    .query(params)
    .json(body)
    .put()
    .badRequest((err) => {
      throw err['json'];
    })
    .res();

export const patchRequest = async (
  url: string,
  params: any = {},
  body: any = {},
  contentType: string = jsonContentType,
): Promise<any> => {
  let service = wService.url(url).query(params);
  if (contentType === formContentType) service = service.formData(body);
  if (contentType === jsonContentType)
    service = service.json(body).content(jsonContentType);
  return await service
    .patch()
    .badRequest((err) => {
      throw err['json'];
    })
    .res();
};

export const deleteRequest = async (
  url: string,
  params: any = {},
): Promise<any> => await wService.url(url).query(params).delete().res();

export const bulkDeleteRequest = async (
  url: string,
  body: any = {},
): Promise<any> =>
  await wService
    .url(url)
    .json(body)
    .delete()
    .badRequest((err) => {
      throw err['json'];
    })
    .json();
